// One-time observability bootstrap. Call once from the app's entry module
// (e.g. src/index.js) BEFORE the first React render. Selects the active sink
// based on REACT_APP_LOG_TRANSPORT and wires unload-flush behavior.
//
// Safe to call multiple times (idempotent).

import { setSink, flush } from './transport';
import { logEnv } from './env';
import { createNewRelicSink, setUnloadFlushMode } from './sinks/newRelicSink';
import { createMemorySink, installDevAccessor } from './sinks/memorySink';

let booted = false;

export function bootObservability(): void {
  if (booted) return;
  booted = true;

  // In dev (and any non-prod env), always keep the memory ring on so
  // developers can inspect via window.__zopeoLogs even when transport=none.
  if (logEnv.env !== 'prod') {
    installDevAccessor();
  }

  switch (logEnv.transport) {
    case 'nr':
    case 'nr+sentry': {
      // Compose: memory (dev only) + NR. Sentry is added in Phase 5.2.
      const nrSink = createNewRelicSink();
      const memSink = logEnv.env !== 'prod' ? createMemorySink() : null;
      setSink((batch) => {
        if (memSink) memSink(batch);
        nrSink(batch);
      });
      break;
    }
    case 'localFile':
    case 'none':
    default: {
      // Memory-only in non-prod; true no-op in prod.
      if (logEnv.env !== 'prod') {
        const memSink = createMemorySink();
        setSink((batch) => memSink(batch));
      }
      break;
    }
  }

  // Unload flush — switch the NR sink into sendBeacon mode then drain.
  if (typeof window !== 'undefined') {
    const onUnload = () => {
      setUnloadFlushMode(true);
      flush();
    };
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
  }
}
